import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import { getUserLocalLogin, useCurrentUser } from "@/lib/auth";

/** Gemeinsame Belegansicht für Suche und Freigabe. */
export type InvoiceBrowserView = "liste" | "kacheln";

const STORAGE_PREFIX = "eh:invoice-browser-view:";

export function parseInvoiceBrowserView(value: unknown): InvoiceBrowserView {
  return value === "liste" ? "liste" : "kacheln";
}

function storageKey(login: string): string {
  return `${STORAGE_PREFIX}${login || "anon"}`;
}

export function readInvoiceBrowserView(login: string): InvoiceBrowserView {
  if (typeof window === "undefined") return "kacheln";
  try {
    return parseInvoiceBrowserView(window.localStorage.getItem(storageKey(login)));
  } catch {
    return "kacheln";
  }
}

export function writeInvoiceBrowserView(login: string, view: InvoiceBrowserView): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(login), view);
  } catch {
    // Privater Modus o. Ä.
  }
}

export async function fetchInvoiceBrowserView(): Promise<{
  view: InvoiceBrowserView;
  hasPreference: boolean;
}> {
  const data = await api.get<{ view_mode?: unknown; has_preference?: unknown }>(
    "/me/invoice-view-preference",
  );
  return {
    view: parseInvoiceBrowserView(data?.view_mode),
    hasPreference: data?.has_preference === true,
  };
}

export async function persistInvoiceBrowserView(view: InvoiceBrowserView): Promise<InvoiceBrowserView> {
  const data = await api.put<{ view_mode?: unknown }>("/me/invoice-view-preference", {
    view_mode: view,
  });
  return parseInvoiceBrowserView(data?.view_mode);
}

export function useInvoiceBrowserView(): {
  view: InvoiceBrowserView;
  setView: (next: InvoiceBrowserView) => void;
} {
  const { user } = useCurrentUser();
  const login = getUserLocalLogin(user);
  const [view, setViewState] = useState<InvoiceBrowserView>(() => readInvoiceBrowserView(login));
  const dirty = useRef(false);

  useEffect(() => {
    dirty.current = false;
    const local = readInvoiceBrowserView(login);
    setViewState(local);
    if (!user) return;

    let cancelled = false;
    void fetchInvoiceBrowserView()
      .then(async ({ view: serverView, hasPreference }) => {
        if (cancelled || dirty.current) return;
        if (!hasPreference) {
          if (local !== "kacheln") {
            try {
              const saved = await persistInvoiceBrowserView(local);
              if (cancelled || dirty.current) return;
              writeInvoiceBrowserView(login, saved);
              setViewState(saved);
            } catch {
              if (!cancelled) setViewState(local);
            }
          }
          return;
        }
        writeInvoiceBrowserView(login, serverView);
        setViewState(serverView);
      })
      .catch(() => {
        if (!cancelled) setViewState(readInvoiceBrowserView(login));
      });

    return () => {
      cancelled = true;
    };
  }, [user, login]);

  const setView = useCallback(
    (next: InvoiceBrowserView) => {
      dirty.current = true;
      setViewState(next);
      writeInvoiceBrowserView(login, next);
      void persistInvoiceBrowserView(next)
        .then((saved) => {
          writeInvoiceBrowserView(login, saved);
          setViewState(saved);
        })
        .catch(() => {
          // Lokale Auswahl bleibt aktiv.
        });
    },
    [login],
  );

  return { view, setView };
}
